<template>
    <a11y-dialog
      :id="`dialog-${_uid}`"
      dialog-root="#dialog-root"
      app-root="[data-award-checkout]"
      :close-button-label="translate('lblDialogClose')"
      :disable-native="true"
      @dialog-ref="assignDialogRef"
      :role="preventClose ? 'alertdialog' : 'dialog'"
      :class-names="classNames"
    >
      <template v-slot:closeButtonContent>
        <icon-close v-if="!preventClose"></icon-close>
      </template>
      <template v-slot:title>{{ title }}</template>

      <div class="dialog-content" :class="type">
        <h2 v-if="type || subtitle">
          <span v-if="type">
            <icon-tick v-if="type === 'success'"></icon-tick>
            <icon-close v-if="type === 'error'"></icon-close>
            <icon-warning v-if="type === 'warning'"></icon-warning>
          </span>
          <span v-if="subtitle" v-text="subtitle"></span>
        </h2>
        <div v-html="content"></div>
        <slot></slot>
      </div>

      <div class="dialog-actions">
        <a v-if="redirectUrl" :href="redirectUrl" class="button button--medium button--primary" v-text="lblBtnOkay"></a>
        <button v-else class="button button--medium button--primary" v-text="lblBtnOkay" @click="emitOkay"></button>
        <button v-if="lblBtnCancel" class="button button--medium button--cancel" v-text="lblBtnCancel" @click="closeDialog"></button>
      </div>
    </a11y-dialog>
</template>

<script>
import { A11yDialog } from 'vue-a11y-dialog';
import IconClose from './icons/Close.vue';
import IconTick from './icons/Tick.vue';
import IconWarning from './icons/Warning.vue';

export default {
  name: 'NotificationModal',
  inheritAttrs: false,
  data: () => ({
    dialog: null,
  }),
  props: {
    title: String,
    subtitle: String,
    content: String,
    type: {
      type: String,
      default: 'error',
    },
    redirectUrl: String,
    lblBtnOkay: {
      type: String,
      default: 'OK',
    },
    lblBtnCancel: String,
    preventClose: Boolean,
  },
  components: {
    A11yDialog,
    IconClose,
    IconTick,
    IconWarning,
  },
  emit: ['close', 'okay'],
  computed: {
    classNames() {
      return {
        closeButton: this.preventClose ? 'disabled' : false,
        element: 'dialog-container',
        overlay: 'dialog-overlay',
      };
    },
  },
  methods: {
    emitOkay() {
      this.$emit('okay');
    },
    closeDialog() {
      this.$emit('close');
    },
    assignDialogRef(dialog) {
      if (dialog) {
        this.dialog = dialog;
        this.dialog.on('hide', () => {
          this.closeDialog();
        });
        this.dialog.show();
      }
    },
  },
};
</script>

<style lang="scss">
.vue-portal-target {

  .dialog-container,
  .dialog-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .dialog-overlay {
    background-color: rgba(11, 11, 11, .8);
    z-index: 2000;
  }

  .dialog-container {
    z-index: 2001;

    // if it's no alert dialog close it in case the user clicks outside of the dialog
    &[role='dialog'] {
      pointer-events: none;
    }

    [role='document'] {
      pointer-events: auto;
      position: absolute;
      top: 50%;
      left: 50%;
      border: none;
      transform: translate(-50%, -50%);
      background-color: $color-white;
      padding: rem(20px);

      [data-a11y-dialog-hide] {
        position: absolute;
        top: rem(20px);
        right: rem(20px);
        width: rem(20px);
        height: rem(20px);
        padding: 0;

        cursor: pointer;
        background: transparent;
        border: 0;
        appearance: none;
        opacity: .65;

        &.disabled {
          display: none;
        }
      }

      h1 {
        font-size: rem(20px);
        margin-bottom: rem(30px);
        line-height: 1;
        font-weight: bold;

        @media (min-width: 768px) {
          font-size: rem(24px);
        }
      }

      .dialog-content {
        padding: rem(20px);
        font-size: rem(16px);

        &.success,
        &.error,
        &.warning {
          h2 {
            font-weight: 700;
            line-height: 1.2;
            text-transform: uppercase;
            display: flex;
            gap: rem(8px);

            span:first-child {
              flex-shrink: 0;
            }
          }
        }

        &.error {
          background-color: $color-kws-signalred-25;

          h2 {
            color: $color-kws-signalred-100;

            svg {
              fill: $color-kws-signalred-100;
            }
          }
        }

        &.warning {
          background-color: $color-kws-yellow-25;

          h2 {
            color: $color-kws-yellow-100;

            svg {
              fill: $color-kws-yellow-100;
            }
          }
        }

        &.success {
          background-color: $color-kws-green-25;

          h2 {
            color: $color-kws-green-100;

            svg {
              fill: $color-kws-green-100;
            }
          }
        }
      }

      .dialog-actions {
        background-color: $color-kws-black-5;
        margin: rem(20px) rem(-20px) rem(-20px);
        padding: rem(20px);

        display: flex;
        justify-content: space-between;
      }

      width: calc(100vw - (2 * #{rem($main-padding-s)}));

      @include media-breakpoint-up(m) {
        width: calc(100vw - (2 * #{rem($main-padding-m)}));
      }
      @include media-breakpoint-up(l) {
        width: calc(100vw - (2 * #{rem($main-padding-l)}));
      }
    }
  }
}
</style>
